import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from '../Common/Modal';
import Button from '../Common/Button';
import { getAssignableRoles } from '../../constants';

const FALLBACK_ROLES = [
  { value: 'Employee', label: 'Employee' },
  { value: 'Admin', label: 'Admin' },
  { value: 'Manager', label: 'Manager' },
  { value: 'Vice President', label: 'Vice President' },
  { value: 'Scrum Master', label: 'Scrum Master' },
];

const EditProjectUserRoleModal = ({
  isOpen,
  onClose,
  user,
  project,
  companyId,
  onSave,
  saving = false,
}) => {
  const [role, setRole] = useState('');
  const [roleOptions, setRoleOptions] = useState(FALLBACK_ROLES);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setError('');
      return undefined;
    }

    let cancelled = false;
    const loadRoles = async () => {
      setLoadingRoles(true);
      try {
        const res = await getAssignableRoles(
          companyId || project?.companyId || user?.companyId || undefined,
        );
        if (cancelled) return;
        const list = res?.data || res || [];
        const options = (Array.isArray(list) ? list : [])
          .map((r) => ({
            value: r.name || r.value,
            label: r.name || r.label,
          }))
          .filter((r) => r.value);
        if (options.length) {
          setRoleOptions(options);
        } else {
          setRoleOptions(FALLBACK_ROLES);
        }
        const current = user?.role || '';
        const match = (options.length ? options : FALLBACK_ROLES).find(
          (o) => String(o.value).toLowerCase() === String(current).toLowerCase(),
        );
        setRole(match?.value || current || (options[0]?.value || 'Employee'));
      } catch {
        if (!cancelled) {
          setRoleOptions(FALLBACK_ROLES);
          setRole(user?.role || 'Employee');
        }
      } finally {
        if (!cancelled) setLoadingRoles(false);
      }
    };

    loadRoles();
    return () => {
      cancelled = true;
    };
  }, [isOpen, user, companyId, project?.companyId]);

  const handleSubmit = async () => {
    if (!role) {
      setError('Select a role.');
      return;
    }
    setError('');
    await onSave(role);
  };

  const displayName = user?.name || user?.email || 'User';
  const projectName = project?.projectName || project?.name || 'Project';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Edit role — ${displayName}`}
      size="medium"
      content={
        <div className="px-4 py-3 space-y-4">
          <p className="text-sm text-[#6B7C93] dark:text-[#8FA3B8]">
            Update role for <b className="text-[#0A2342] dark:text-white">{displayName}</b> on{' '}
            <b className="text-[#0A2342] dark:text-white">{projectName}</b>. Privileges will match the
            selected role after they refresh or log in again.
          </p>

          {user?.email ? (
            <p className="text-xs text-[#6B7C93] dark:text-[#8FA3B8]">{user.email}</p>
          ) : null}

          <div>
            <label className="block text-sm font-medium text-[#0A2342] dark:text-white mb-1.5">
              Role
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              disabled={loadingRoles || !roleOptions.length || saving}
              className="w-full px-3 py-2.5 rounded-md border border-[#D1E2F0] dark:border-[#25384F] bg-white dark:bg-[#151F2C] text-[#0A2342] dark:text-white text-sm outline-none focus:border-[#326AEB] disabled:opacity-60"
            >
              {loadingRoles && <option value="">Loading roles...</option>}
              {!loadingRoles &&
                roleOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
            </select>
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>
      }
      actions={
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={saving || !role || loadingRoles}
          >
            {saving ? 'Saving...' : 'Save role'}
          </Button>
        </div>
      }
    />
  );
};

EditProjectUserRoleModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.object,
  project: PropTypes.object,
  companyId: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
};

EditProjectUserRoleModal.defaultProps = {
  user: null,
  project: null,
  companyId: '',
  saving: false,
};

export default EditProjectUserRoleModal;
